import classNames from "classnames";
import { GridColumn } from "../blocks/layout/GridColumn";
import { StageRenderer } from "../render/StageRenderer";
import { PageBlock, StageRow } from "../config/pageRows.types";
import { RelatedTables } from "./RelatedTables";
import { useEntityPage } from "./useEntityPage";

/**
 * The rows, columns and blocks of the page - stages and tables alike. A page that lays
 * out some of its rows itself passes the ones this should draw.
 */
export function EntityPageContent({
  rows: shownRows,
  flat = false,
}: {
  rows?: StageRow[];
  /** Draws the stages with no card, gutters or page padding, for a pane of their own. */
  flat?: boolean;
}) {
  const {
    record,
    rows: pageRows,
    isNew,
    canEdit,
    fieldErrors,
    onFieldChange,
  } = useEntityPage();
  const rows = shownRows ?? pageRows;
  const values = record ?? {};

  const renderBlock = (block: PageBlock, key: number) => {
    if (block.kind === "stage") {
      return (
        <StageRenderer
          key={key}
          stage={block.stage}
          values={values}
          onChange={onFieldChange}
          errors={fieldErrors}
          canEdit={canEdit}
          flat={flat}
        />
      );
    }

    // A new record has no related records yet, and a tables block need not have
    // been given any tabs - either way the block renders nothing, unless its tabs ask
    // to be shown empty on a new record.
    if (
      block.tabs.length === 0 ||
      (isNew && !block.tabs.every((tab) => tab.showWhenNew))
    ) {
      return null;
    }

    return <RelatedTables key={key} tabs={block.tabs} title={block.title} />;
  };

  return (
    <div
      className={classNames(
        "d-flex flex-column flex-grow-1 position-relative",
        flat ? "gap-3" : "gap-5 py-3 px-3 py-md-4 px-md-4"
      )}
    >
      {rows.map((row, rowIndex) => (
        <div
          className={classNames("row", flat ? "gx-0" : "gx-5 row-gap-5")}
          key={rowIndex}
        >
          {row.columns.map((column, columnIndex) => (
            <GridColumn
              key={columnIndex}
              // A card column is a flex row holding one card, so the card can be told to
              // grow; a plain one stacks its sections itself.
              span={column.span}
              className={classNames(
                column.card ? "d-flex" : "d-flex flex-column gap-5",
                flat && "p-2"
              )}
            >
              {column.card ? (
                <div
                  className={classNames(
                    "page-column-card d-flex flex-column gap-4 w-100",
                    column.fillHeight && "flex-grow-1"
                  )}
                >
                  {column.blocks.map(renderBlock)}
                </div>
              ) : (
                column.blocks.map(renderBlock)
              )}
            </GridColumn>
          ))}
        </div>
      ))}
    </div>
  );
}
export default EntityPageContent;
